/**
 * Phase 17 — IC Bounds shared section (target + interceptor XYZ
 * min/max blocks). Reused across all campaign types in the unified
 * builder: when both ranges on an axis are equal it's a fixed point;
 * otherwise per-MC-sample IC drawn from those bounds.
 */
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const AXES = [
    { id: "x_m", label: "X (East, m)" },
    { id: "y_m", label: "Y (North, m)" },
    { id: "z_m", label: "Z (Up, m)" },
];

function _row(value, onChange, axis, role) {
    const v = value || {};
    const lo = v.lo ?? "";
    const hi = v.hi ?? "";
    return (
        <div className="grid grid-cols-3 gap-2 items-end" key={axis.id}>
            <Label className="text-xs text-ux-text-3 col-span-1">
                {axis.label}
            </Label>
            <div>
                <Label
                    className="block text-[10px] text-ux-text-3"
                    htmlFor={`${role}-${axis.id}-lo`}
                >
                    min
                </Label>
                <Input
                    id={`${role}-${axis.id}-lo`}
                    type="number"
                    value={lo}
                    onChange={(e) => onChange({
                        lo: e.target.value === "" ? null : Number(e.target.value),
                        hi: hi === "" ? null : Number(hi),
                    })}
                    data-testid={`ic-${role}-${axis.id}-lo`}
                />
            </div>
            <div>
                <Label
                    className="block text-[10px] text-ux-text-3"
                    htmlFor={`${role}-${axis.id}-hi`}
                >
                    max
                </Label>
                <Input
                    id={`${role}-${axis.id}-hi`}
                    type="number"
                    value={hi}
                    onChange={(e) => onChange({
                        lo: lo === "" ? null : Number(lo),
                        hi: e.target.value === "" ? null : Number(e.target.value),
                    })}
                    data-testid={`ic-${role}-${axis.id}-hi`}
                />
            </div>
        </div>
    );
}

function _block({ label, role, enabled, onToggle, value, onChange }) {
    return (
        <div
            className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-3 space-y-2"
            data-testid={`ic-${role}-block`}
            data-enabled={enabled ? "1" : "0"}
        >
            <div className="flex items-center gap-2">
                <Switch
                    checked={enabled}
                    onCheckedChange={onToggle}
                    data-testid={`ic-${role}-toggle`}
                />
                <span className="text-sm font-medium text-ux-text-1">
                    {label}
                </span>
            </div>
            {enabled && (
                <div className="space-y-2">
                    {AXES.map((ax) => _row(
                        value?.[ax.id],
                        (next) => {
                            const cur = { ...(value || {}) };
                            cur[ax.id] = (next.lo === null && next.hi === null)
                                ? null
                                : { lo: next.lo ?? next.hi ?? 0, hi: next.hi ?? next.lo ?? 0 };
                            if (cur[ax.id] && (cur[ax.id].lo === null || cur[ax.id].hi === null)) {
                                cur[ax.id] = null;
                            }
                            onChange(cur);
                        },
                        ax,
                        role,
                    ))}
                    <p className="text-[11px] text-ux-text-3">
                        Min == max → axis fixed at that value. Per-sample drawn
                        from [min, max] otherwise.
                    </p>
                </div>
            )}
        </div>
    );
}

export default function ICBoundsSection({
    targetEnabled, onTargetToggle,
    targetValue, onTargetChange,
    interceptorEnabled, onInterceptorToggle,
    interceptorValue, onInterceptorChange,
}) {
    return (
        <section
            className="space-y-3"
            data-testid="section-ic-bounds"
        >
            {_block({
                label: "Target IC bounds",
                role: "target",
                enabled: targetEnabled,
                onToggle: onTargetToggle,
                value: targetValue,
                onChange: onTargetChange,
            })}
            {_block({
                label: "Interceptor IC bounds",
                role: "interceptor",
                enabled: interceptorEnabled,
                onToggle: onInterceptorToggle,
                value: interceptorValue,
                onChange: onInterceptorChange,
            })}
        </section>
    );
}
